/**
 * Shared chrome for public Support docs — matches DSS app shell, no marketing hero.
 */
import React from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  Link,
  Divider,
  AppBar,
  Toolbar,
  IconButton,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import Logo from '../common/Logo';
import type { RootState } from '../../store';

const year = new Date().getFullYear();

interface SupportShellProps {
  title: string;
  subtitle?: string;
  /** Back target; default /support; use "/" for support index */
  backTo?: string;
  backLabel?: string;
  children: React.ReactNode;
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl';
}

const SupportShell: React.FC<SupportShellProps> = ({
  title,
  subtitle,
  backTo = '/support',
  backLabel,
  children,
  maxWidth = 'lg',
}) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useSelector((s: RootState) => s.auth);

  const resolvedBack = backTo;
  const resolvedBackLabel =
    backLabel || (resolvedBack === '/' ? 'Home' : 'Support');

  return (
    <Box
      component="main"
      id="main-content"
      tabIndex={-1}
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.default',
        outline: 'none',
      }}
    >
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: 'background.paper',
          color: 'text.primary',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Toolbar variant="dense" sx={{ minHeight: 52, gap: 1 }}>
          <IconButton
            edge="start"
            size="small"
            onClick={() => navigate(resolvedBack)}
            aria-label={`Back to ${resolvedBackLabel}`}
          >
            <ArrowBack fontSize="small" />
          </IconButton>

          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ flexGrow: 1, minWidth: 0 }}
            component={RouterLink}
            to={isAuthenticated ? '/dashboard' : '/'}
            style={{ textDecoration: 'none', color: 'inherit' }}
          >
            <Logo type="flahafast" size="small" />
            <Typography
              variant="subtitle2"
              fontWeight={700}
              color="primary.main"
              noWrap
            >
              FlahaFAST
              <Typography
                component="span"
                variant="caption"
                color="text.secondary"
                sx={{ ml: 0.75, fontWeight: 500 }}
              >
                Support
              </Typography>
            </Typography>
          </Stack>

          {isAuthenticated ? (
            <Button
              size="small"
              variant="contained"
              onClick={() => navigate('/dashboard')}
            >
              Workspace
            </Button>
          ) : (
            <Button
              size="small"
              variant="outlined"
              onClick={() => navigate('/login')}
            >
              Sign in
            </Button>
          )}
        </Toolbar>
      </AppBar>

      <Box component="main" id="main-content" tabIndex={-1} sx={{ flex: 1, py: 3 }}>
        <Container maxWidth={maxWidth}>
          <Box sx={{ mb: 2.5 }}>
            <Typography variant="h5" component="h1" fontWeight={600} gutterBottom>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 640 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          {children}
        </Container>
      </Box>

      <Box
        component="footer"
        sx={{
          borderTop: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          py: 1.5,
        }}
      >
        <Container maxWidth="lg">
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
          >
            <Typography variant="caption" color="text.secondary">
              FlahaFast DSS · Nutrient formulation decision support
            </Typography>
            <Stack
              direction="row"
              spacing={1.5}
              flexWrap="wrap"
              useFlexGap
              sx={{
                '& a': {
                  typography: 'caption',
                  color: 'text.secondary',
                  textDecoration: 'none',
                  '&:hover': { color: 'primary.main' },
                },
              }}
            >
              <Link component={RouterLink} to="/support">
                Docs home
              </Link>
              <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
              <Link component={RouterLink} to={isAuthenticated ? '/dashboard' : '/'}>
                {isAuthenticated ? 'Workspace' : 'Home'}
              </Link>
              <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
              <Typography variant="caption" color="text.disabled">
                © {year} Flaha Agri Tech
              </Typography>
            </Stack>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
};

export default SupportShell;
